import type { ComponentTotals, EditableComponent, SuggestionGains } from "./coach-attributed-score";
import { EDITABLE_COMPONENTS, isEditableComponent } from "./coach-attributed-score";

/**
 * Scale the model's claimed gains down to the headroom that actually exists.
 *
 * MIRRORS lms-backend/src/services/placement-services/coachResumeProjection.util.js
 * `normalizeGainsToHeadroom`. The LMS runs the same function over the same plan when the
 * student saves, so if these two drift the builder shows one number and the job card stores
 * another — the exact failure this whole change exists to remove. Change one, change both;
 * coach-gain-distribution.spec.ts pins the shared cases.
 *
 * ── WHY THIS IS NEEDED ─────────────────────────────────────────────────────────────────
 *
 * The model states a `gain` per suggestion in sub-score points, but it has no idea how much
 * room is left on each component. Against a résumé already sitting at `skillOverlap: 70` it
 * will happily offer six skills at 8 points each — 48 points into a 30-point gap.
 *
 * `projectTotalScore` then clamps the sub-score at 100 SILENTLY. So 18 of those points
 * vanish inside the clamp while every card still advertises its own "+8", and `potential`
 * ("accept everything to reach N") is computed from the same over-claim. The student is
 * promised a number the scorer cannot produce, and the score they end up with is lower than
 * the one they were watching.
 *
 * ── WHAT IT DOES ───────────────────────────────────────────────────────────────────────
 *
 * Each component's claims are scaled by `headroom / claimed` when they overflow. The
 * DISTRIBUTION is preserved: a suggestion the model rated twice as valuable as another still
 * earns twice as much, so "suggestions carry their own weight" survives the correction. Only
 * the total is bounded, which is what makes the per-card number, the projection and the
 * persisted score agree.
 *
 * Under-claims are deliberately left alone. If the model is modest about what it can
 * achieve, inflating its numbers to fill the headroom would be inventing points nobody
 * justified.
 *
 * ⚠️ RUN THIS AFTER THE FILTERS, NOT BEFORE. Suggestions dropped by `verifySuggestions`,
 * `dropNoOpSuggestions` or `classifySkillAdditions` free up headroom. Normalizing first
 * would scale the survivors down to make room for cards the student never sees, understating
 * every one of them.
 */

/** Anything carrying an attributed gain: a text rewrite or a skill addition. */
type Gainful = { id: string; gain?: number };

export type GainDistribution<S extends Gainful, A extends Gainful> = {
  gains: SuggestionGains;
  suggestions: S[];
  skillAdditions: A[];
  /** Factor applied per component, 1 when nothing was scaled. Logged, not shown. */
  scaled: Partial<Record<EditableComponent, number>>;
  /** Ids dropped because their component had no room left for them. */
  dropped: string[];
};

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * @param baseline current sub-scores, from the LMS for the student flow
 */
export const distributeGains = <S extends Gainful, A extends Gainful>({
  gains,
  suggestions,
  skillAdditions,
  baseline,
}: {
  gains: SuggestionGains;
  suggestions: S[];
  skillAdditions: A[];
  baseline: ComponentTotals;
}): GainDistribution<S, A> => {
  const claimed: Partial<Record<EditableComponent, number>> = {};

  for (const entry of Object.values(gains)) {
    if (!entry || !isEditableComponent(entry.targets)) continue;

    const gain = Number(entry.gain);
    if (!Number.isFinite(gain) || gain <= 0) continue;

    claimed[entry.targets] = (claimed[entry.targets] ?? 0) + gain;
  }

  const scaled: Partial<Record<EditableComponent, number>> = {};

  for (const component of EDITABLE_COMPONENTS) {
    const total = claimed[component] ?? 0;
    if (total <= 0) continue;

    const current = Math.min(100, Math.max(0, Number(baseline[component]) || 0));
    const headroom = 100 - current;

    scaled[component] = total > headroom ? headroom / total : 1;
  }

  const nextGains: SuggestionGains = {};
  const dropped: string[] = [];

  for (const [id, entry] of Object.entries(gains)) {
    if (!entry || !isEditableComponent(entry.targets)) continue;

    const gain = Number(entry.gain);
    if (!Number.isFinite(gain) || gain <= 0) continue;

    const adjusted = round2(gain * (scaled[entry.targets] ?? 1));

    // Rounded to nothing means the component is full and this suggestion cannot earn from
    // it. Dropping it keeps `gains` as the single definition of "this id can earn points",
    // which is what `use-coach-live-score` filters `totalCount` on — a "+0" card would
    // otherwise sit in the count and stop "All suggestions applied" from ever being true.
    if (adjusted <= 0) {
      dropped.push(id);
      continue;
    }

    nextGains[id] = { targets: entry.targets, gain: adjusted };
  }

  /**
   * Rewritten on the entries too, not just in `gains`.
   *
   * The cards read `suggestion.gain` for their own "+N" while the donut reads `gains`. Fixing
   * only one of them would have a card promising +8 next to a projection that moved by 5 —
   * a discrepancy visible in a single screenshot.
   */
  const applyTo = <T extends Gainful>(entries: T[]): T[] =>
    entries.map((entry) => {
      const next = nextGains[entry.id];
      if (next) return { ...entry, gain: next.gain };

      // No entry: either it was dropped just now, or it never had a gain at all — an
      // unscoreable rewrite, which `parseCoachPlan` already sets to 0 on purpose.
      return dropped.includes(entry.id) ? { ...entry, gain: 0 } : entry;
    });

  return {
    gains: nextGains,
    suggestions: applyTo(suggestions),
    skillAdditions: applyTo(skillAdditions),
    scaled,
    dropped,
  };
};
